<script>
import { computed, watchEffect } from 'vue';
import { useRoute } from 'vue-router';
import {
  getLearnArticleBySlug,
  getZhLearnArticleBySlug,
  getArticleTranslation
} from '@/assets/seo/learnArticles';
import {
  SITE_URL,
  pageMeta,
  fallbackMaterialCategory,
  materialPath,
  parseTestParam,
  materialCategoryMeta,
  materialCollectionMeta,
  materialTestMeta
} from '@/assets/seo/pageMeta';
import { getFallbackCategory } from '@/assets/material/materialCatalog';
import { currentMaterialCategory, currentMaterialCollection } from '@/composables/materialSeo';

const noIndexRouteNames = new Set(['PaymentSuccess', 'PaymentCancel', 'MaterialAdmin', 'Login', 'Signup', 'ResetPassword', 'CheckIn', 'Profile', 'ZhProfile', 'LevelTest', 'ZhLevelTest']);

function materialCategoryFor(categoryId) {
  const loaded = currentMaterialCategory.value;
  return getFallbackCategory(categoryId) || fallbackMaterialCategory(categoryId, loaded?.id === categoryId ? loaded : null);
}

function getAbsoluteUrl(path) {
  return `${SITE_URL}${path || '/'}`;
}

function setMetaTag(selector, attributes) {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    document.head.appendChild(tag);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    tag.setAttribute(key, value);
  });
}

function setCanonical(url) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
}

function setAlternateLinks(alternates) {
  document.head.querySelectorAll('link[data-seo-hreflang]').forEach(link => link.remove());

  alternates.forEach(({ hreflang, path }) => {
    const link = document.createElement('link');
    link.setAttribute('rel', 'alternate');
    link.setAttribute('hreflang', hreflang);
    link.setAttribute('href', getAbsoluteUrl(path));
    link.setAttribute('data-seo-hreflang', 'true');
    document.head.appendChild(link);
  });
}

export default {
  name: 'SEOHeadTags',
  props: {
    currentView: {
      type: String,
      default: 'HomePage'
    },
    currentViewName: {
      type: String,
      default: ''
    },
    currentViewPath: {
      type: String,
      default: ''
    }
  },
  setup() {
    const route = useRoute();

    const routeMeta = computed(() => {
      if (route.meta.page === 'LevelTest') return { title: 'English level test | GoodScriber', description: 'A short listening and speaking test that places you between A1 and C2.', path: route.path };
      if (route.meta.page === 'Profile') return { title: 'Learning preferences | GoodScriber', description: 'Your listening and speaking levels, interests and learning goals.', path: route.path };
      if (route.name === 'CheckIn') return { title: 'Study streak | GoodScriber', description: 'Your daily listening goals and learning progress.', path: '/check-in' };
      if (route.meta.creationTool) {
        const titles = { transcribe: 'Audio & video transcription', youtube: 'Import YouTube', speech: 'Text to speech' };
        return { title: `${titles[route.meta.creationTool]} | GoodScriber`, description: 'Create your own listening and speaking practice material.', path: route.path };
      }
      if (route.name === 'LearnArticle' || route.name === 'ZhLearnArticle') {
        const article = route.name === 'ZhLearnArticle'
          ? getZhLearnArticleBySlug(route.params.slug)
          : getLearnArticleBySlug(route.params.slug);
        if (article) {
          const prefix = route.name === 'ZhLearnArticle' ? '/zh/learn' : '/learn';
          return {
            title: `${article.title} | GoodScriber`,
            description: article.description,
            path: `${prefix}/${article.slug}`
          };
        }
      }

      // 素材页分中英两套地址，route.meta.locale 决定标题和描述用哪种语言
      if (route.meta.page === 'MaterialCategory') {
        return materialCategoryMeta(materialCategoryFor(String(route.params.id)), route.meta.locale);
      }

      if (route.meta.page === 'MaterialCollection' || route.meta.page === 'MaterialTest') {
        const language = route.meta.locale;
        const category = materialCategoryFor(String(route.params.id));
        const collection = currentMaterialCollection.value;
        const test = parseTestParam(route.params.test);
        if (collection && collection.categoryId === category.id && collection.id === route.params.collectionId) {
          return test
            ? materialTestMeta(category, collection, test, language)
            : materialCollectionMeta(category, collection, language);
        }
        // 合集标题还没从接口加载回来时，先用分类信息，地址用当前页自己的地址
        return {
          ...materialCategoryMeta(category, language),
          path: materialPath(language, category.id, route.params.collectionId, test)
        };
      }

      return pageMeta[route.name] || pageMeta.Home;
    });

    const canonicalUrl = computed(() => getAbsoluteUrl(routeMeta.value.path));
    const socialImageUrl = computed(() => (
      `${SITE_URL}/assets/og_images/${/^\/zh(\/|$)/.test(routeMeta.value.path) ? 'og-image.png' : 'og-image-en.png'}`
    ));
    const learnArticle = computed(() => (
      route.name === 'ZhLearnArticle'
        ? getZhLearnArticleBySlug(route.params.slug)
        : route.name === 'LearnArticle'
          ? getLearnArticleBySlug(route.params.slug)
          : null
    ));
    const shouldNoIndex = computed(() => (
      noIndexRouteNames.has(route.name) || ((route.name === 'LearnArticle' || route.name === 'ZhLearnArticle') && !learnArticle.value)
    ));
    const alternateLinks = computed(() => {
      // 首页和素材页：中文地址 = /zh + 英文地址
      if (route.meta.page) {
        const path = routeMeta.value.path;
        const englishPath = route.meta.locale === 'zh' ? path.replace(/^\/zh/, '') || '/' : path;
        return [
          { hreflang: 'en', path: englishPath },
          { hreflang: 'zh-CN', path: englishPath === '/' ? '/zh' : `/zh${englishPath}` },
          { hreflang: 'x-default', path: englishPath }
        ];
      }

      if (route.name === 'LearnIndex' || route.name === 'ZhLearnIndex') {
        return [
          { hreflang: 'en', path: '/learn' },
          { hreflang: 'zh-CN', path: '/zh/learn' },
          { hreflang: 'x-default', path: '/learn' }
        ];
      }

      if (route.name === 'LearnArticle' || route.name === 'ZhLearnArticle') {
        const language = route.name === 'ZhLearnArticle' ? 'zh' : 'en';
        const translation = getArticleTranslation(language, route.params.slug);
        if (!translation) return [];

        const currentPath = routeMeta.value.path;
        const englishPath = language === 'en' ? currentPath : translation.path;
        const chinesePath = language === 'zh' ? currentPath : translation.path;
        return [
          { hreflang: 'en', path: englishPath },
          { hreflang: 'zh-CN', path: chinesePath },
          { hreflang: 'x-default', path: englishPath }
        ];
      }

      return [];
    });

    const breadcrumbSchema = computed(() => {
      if (route.name === 'Home' || route.name === 'ZhHome' || shouldNoIndex.value) return null;

      if (route.name === 'LearnArticle' || route.name === 'ZhLearnArticle') {
        const isChinese = route.name === 'ZhLearnArticle';
        const guidePath = isChinese ? '/zh/learn' : '/learn';
        return {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": `${SITE_URL}/`
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": isChinese ? "中文学习指南" : "Learning Guides",
              "item": `${SITE_URL}${guidePath}`
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": routeMeta.value.title.replace(' | GoodScriber', ''),
              "item": canonicalUrl.value
            }
          ]
        };
      }
      
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": `${SITE_URL}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": routeMeta.value.title.replace(' | GoodScriber', ''),
            "item": canonicalUrl.value
          }
        ]
      };
    });

    watchEffect(() => {
      const meta = routeMeta.value;
      const robots = shouldNoIndex.value ? 'noindex, nofollow' : 'index, follow';

      document.title = meta.title;
      setMetaTag('meta[name="description"]', { name: 'description', content: meta.description });
      setMetaTag('meta[name="robots"]', { name: 'robots', content: robots });
      setMetaTag('meta[property="og:type"]', { property: 'og:type', content: learnArticle.value ? 'article' : 'website' });
      setMetaTag('meta[property="og:url"]', { property: 'og:url', content: canonicalUrl.value });
      setMetaTag('meta[property="og:title"]', { property: 'og:title', content: meta.title });
      setMetaTag('meta[property="og:description"]', { property: 'og:description', content: meta.description });
      setMetaTag('meta[property="og:image"]', { property: 'og:image', content: socialImageUrl.value });
      setMetaTag('meta[property="og:image:width"]', { property: 'og:image:width', content: '1200' });
      setMetaTag('meta[property="og:image:height"]', { property: 'og:image:height', content: '630' });
      setMetaTag('meta[name="twitter:url"]', { name: 'twitter:url', content: canonicalUrl.value });
      setMetaTag('meta[name="twitter:title"]', { name: 'twitter:title', content: meta.title });
      setMetaTag('meta[name="twitter:description"]', { name: 'twitter:description', content: meta.description });
      setMetaTag('meta[name="twitter:image"]', { name: 'twitter:image', content: socialImageUrl.value });
      setCanonical(canonicalUrl.value);
      setAlternateLinks(alternateLinks.value);

      let breadcrumbTag = document.getElementById('breadcrumb-schema');
      if (!breadcrumbTag) {
        breadcrumbTag = document.createElement('script');
        breadcrumbTag.id = 'breadcrumb-schema';
        breadcrumbTag.type = 'application/ld+json';
        document.head.appendChild(breadcrumbTag);
      }

      // Update the schema or remove if not needed
      if (breadcrumbSchema.value) {
        breadcrumbTag.textContent = JSON.stringify(breadcrumbSchema.value);
      } else if (breadcrumbTag.parentNode) {
        breadcrumbTag.parentNode.removeChild(breadcrumbTag);
      }

      let articleTag = document.getElementById('learn-article-schema');
      let faqTag = document.getElementById('learn-faq-schema');
      const article = learnArticle.value;

      if (article) {
        if (!articleTag) {
          articleTag = document.createElement('script');
          articleTag.id = 'learn-article-schema';
          articleTag.type = 'application/ld+json';
          document.head.appendChild(articleTag);
        }
        if (!faqTag) {
          faqTag = document.createElement('script');
          faqTag.id = 'learn-faq-schema';
          faqTag.type = 'application/ld+json';
          document.head.appendChild(faqTag);
        }

        articleTag.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.description,
          mainEntityOfPage: canonicalUrl.value,
          image: socialImageUrl.value,
          inLanguage: route.name === 'ZhLearnArticle' ? 'zh-CN' : 'en',
          datePublished: article.publishedAt,
          dateModified: article.updatedAt,
          author: {
            '@type': article.author.type,
            name: article.author.name,
            url: article.author.url
          },
          publisher: {
            '@type': 'Organization',
            name: 'GoodScriber',
            logo: {
              '@type': 'ImageObject',
              url: `${SITE_URL}/android-chrome-512x512.png`,
              width: 512,
              height: 512
            }
          },
          ...(article.sources.length ? { citation: article.sources.map(source => source.url) } : {})
        });

        faqTag.textContent = JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: article.faqs.map(([question, answer]) => ({
            '@type': 'Question',
            name: question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: answer
            }
          }))
        });
      } else {
        if (articleTag?.parentNode) articleTag.parentNode.removeChild(articleTag);
        if (faqTag?.parentNode) faqTag.parentNode.removeChild(faqTag);
      }
    });

    return {};
  },
  render() {
    // This component doesn't render anything visible
    return null;
  }
};
</script> 
